import { CircularProgress } from "@mui/material";
import { FetchPredictedDataEvent, useDebouncedEventTracking } from "@shared/charts/tracking";
import { Series } from "@shared/charts/types";
import { useRemergeSwrResponse } from "@shared/http/RemergeApiSwr";
import {
  METRICS_TO_DISPLAY,
  SherlockChangeDetails,
  SherlockChangeType,
  SherlockDruidDataAttributes,
  SherlockImpact,
  SherlockMetricType,
} from "@shared/http/apiTypes/sherlockApiTypes";
import { RemergeAPIContext } from "@shared/http/remergeApiContext";
import classNames from "classnames";
import { useContext, useEffect, useRef, useState } from "react";
import { useSherlockTracking } from "../hooks/useSherlockTracking";
import classes from "./InvestigationResult.module.css";
import SherlockResultsChart from "./SherlockResultsChart";
import SherlockInvestigationResultLegend from "./SherlockResultsChartLegend";

const MAIN_COLOR = "#D0C8FB";
const IMPACT_COLOR = "#000";
export const SHERLOCK_ACTUAL_DATA_LABEL = "Actual data";
export const SHERLOCK_IMPACT_DATA_LABEL = "Impact";
export const SHERLOCK_BASELINE_DATA_LABEL = "Baseline";

type Props = {
  selectedMetric: SherlockMetricType;
  startDate: string;
  endDate: string;
  selectedOrganization: string;
  selectedCampaign: string;
  selectedChanges: SherlockChangeType[];
  resultsError: string | null;
  setResultsError: React.Dispatch<React.SetStateAction<string | null>>;
  currencySymbol: string;
  userId: string;
};

/**
 * InvestigationResult - Displays Sherlock investigation results with charts and analysis
 * Used in: Sherlock UI for showing data analysis and change impact predictions
 *
 * Key responsibilities:
 * - Fetches and displays actual data with changes
 * - Manages Sherlock result predictions and impact analysis
 * - Handles data validation and error states
 * - Renders interactive charts with baseline and impact data
 */
export default function InvestigationResult({
  startDate,
  endDate,
  selectedMetric,
  selectedOrganization,
  selectedCampaign,
  selectedChanges,
  resultsError,
  setResultsError,
  currencySymbol,
  userId,
}: Props) {
  // Deps

  const remergeApi = useContext(RemergeAPIContext);
  const trackEvent = useDebouncedEventTracking(5000);
  const { trackSherlockImpactMessageDisplayed } = useSherlockTracking(userId);

  // State

  const [selectedChangeId, setSelectedChangeId] = useState<string | undefined>(undefined);
  const [resultData, setResultData] = useState<Series<SherlockChangeDetails[]>[]>([]);
  const [isSignificant, setisSignificant] = useState<boolean>(false);
  const [impact, setImpact] = useState<SherlockImpact | null>(null);
  const [dataValidationError, setDataValidationError] = useState<string | null>(null);
  const previousDataRef = useRef<SherlockDruidDataAttributes[] | null>(null);
  const [impactMessage, setImpactMessage] = useState<string>("");

  // Computed State

  const actualDataWithChanges = useRemergeSwrResponse(
    selectedCampaign
      ? remergeApi.actualDataWithChanges(
          selectedCampaign,
          startDate as string,
          endDate as string,
          selectedMetric,
          selectedChanges,
        )
      : null,
  );

  const { data: sherlockResult, isLoading } = useRemergeSwrResponse(
    selectedChangeId
      ? remergeApi.sherlockResult({
          campaign_id: selectedCampaign,
          start_date: startDate,
          end_date: endDate,
          metric: selectedMetric,
          change_id: selectedChangeId,
          changes: selectedChanges,
        })
      : null,
    {
      manualErrorHandling: true,
    },
  );

  const listOfChanges = selectedChanges.join(",");
  const metricString = METRICS_TO_DISPLAY[selectedMetric];
  const value = impact?.value;

  const impactValueMessage = sherlockResult?.predicted_impact && (
    <div>
      <div
        className={classNames(
          isSignificant && value ? classes.significantImpact : classes.insignificantImpact,
          classes.resultMessageContainer,
        )}
      >
        {impactMessage}
      </div>
    </div>
  );

  // Effects

  useEffect(resetStateOnSelectionChange, [selectedMetric, selectedCampaign, listOfChanges, setResultsError]);

  useEffect(handleActualDataResponse, [actualDataWithChanges.data, setResultsError]);

  useEffect(handleSherlockResultResponse, [
    sherlockResult,
    selectedChangeId,
    selectedMetric,
    startDate,
    endDate,
    trackEvent,
    isSignificant,
    actualDataWithChanges.data,
    setResultsError,
    trackSherlockImpactMessageDisplayed,
    currencySymbol,
    impactMessage,
  ]);

  return (
    <>
      {selectedCampaign && <SherlockInvestigationResultLegend />}

      <div className={classes.resultMessageContainer}>
        {dataValidationError ? (
          <div className={classNames(classes.resultNotAvailable, classes.resultMessageContainer)}>
            {dataValidationError}
          </div>
        ) : resultsError ? (
          <div className={classNames(classes.resultNotAvailable, classes.resultMessageContainer)}>{resultsError}</div>
        ) : (
          impactValueMessage
        )}
      </div>

      <div className={classes.isLoading}>{(actualDataWithChanges.isLoading || isLoading) && <CircularProgress />}</div>

      {!dataValidationError && (
        <SherlockResultsChart
          series={resultData}
          isSignificant={isSignificant}
          yAxisLabel={metricString}
          onEventClick={handleChangeSelect}
          selectedOrganization={selectedOrganization}
          selectedCampaign={selectedCampaign}
          selectedMetric={selectedMetric}
          currencySymbol={currencySymbol}
          userId={userId}
        />
      )}
    </>
  );

  // Functions

  function resetStateOnSelectionChange() {
    setisSignificant(false);
    setImpact(null);
    setSelectedChangeId(undefined);
    setResultsError(null);
    setDataValidationError(null);
    previousDataRef.current = null;
  }

  function handleActualDataResponse() {
    if (actualDataWithChanges.data !== undefined) {
      if (!isValidDataArray(actualDataWithChanges.data)) {
        const errorMessage = "No data available for the selected criteria";
        setDataValidationError(errorMessage);
        setResultData([]);
        previousDataRef.current = null;
        return;
      }

      setDataValidationError(null);

      const actualDataHasChanged =
        JSON.stringify(actualDataWithChanges.data) !== JSON.stringify(previousDataRef.current);

      if (actualDataHasChanged) {
        setSelectedChangeId(undefined);
        setResultsError(null);
        setImpact(null);

        previousDataRef.current = actualDataWithChanges.data;

        const newData = [
          {
            label: SHERLOCK_ACTUAL_DATA_LABEL,
            color: MAIN_COLOR,
            values: parseDates(actualDataWithChanges.data),
          },
        ];

        setResultData(newData);
      }
    }
  }

  function handleSherlockResultResponse() {
    if (sherlockResult?.error) {
      setResultsError(sherlockResult?.error);
      setImpact(null);

      if (actualDataWithChanges.data && isValidDataArray(actualDataWithChanges.data)) {
        const newData = [
          {
            label: SHERLOCK_ACTUAL_DATA_LABEL,
            color: MAIN_COLOR,
            values: parseDates(actualDataWithChanges.data),
          },
        ];

        setResultData(newData);
      }
    }

    if (sherlockResult && !sherlockResult.error && selectedChangeId) {
      setResultsError(null);

      if (isSignificant !== sherlockResult.is_significant) {
        setisSignificant(sherlockResult.is_significant);
      }

      const hasValidPredictedImpact = isValidDataArray(sherlockResult.predicted_impact);
      const hasValidBaseline = isValidDataArray(sherlockResult.baseline);

      if (!hasValidPredictedImpact || !hasValidBaseline) {
        setResultsError("Prediction data is not available for the selected change.");
        return;
      }

      const result = [
        {
          label: SHERLOCK_IMPACT_DATA_LABEL,
          color: IMPACT_COLOR,
          values: parseDates(sherlockResult.predicted_impact),
        },
        {
          label: SHERLOCK_BASELINE_DATA_LABEL,
          color: MAIN_COLOR,
          values: parseDates(sherlockResult.baseline),
        },
      ];
      setResultData(result);
      setImpact(sherlockResult.impact);

      if (sherlockResult.impact) {
        if (isSignificant) {
          const { value, days, hours } = sherlockResult.impact;
          const metricString = METRICS_TO_DISPLAY[selectedMetric];
          const currency = metricString === "Spend" ? currencySymbol : "";
          const messageText =
            value && value > 0
              ? `Increase of ${currency}${value} ${metricString} over ${days} days and ${hours} hours`
              : `Decrease of ${currency}${value} ${metricString} over ${days} days and ${hours} hours`;
          setImpactMessage(messageText);
        } else {
          setImpactMessage("Not significant");
        }

        trackSherlockImpactMessageDisplayed(impactMessage);
      }

      if (selectedChangeId) {
        trackEvent(
          new FetchPredictedDataEvent({
            "Change ID": selectedChangeId,
            Metric: selectedMetric,
            "Date Range Start": startDate,
            "Date Range End": endDate,
          }),
        );
      }
    }
  }

  function handleChangeSelect(val: string | null) {
    if (val === selectedChangeId) return;
    setResultsError(null);

    if (val) setSelectedChangeId(val);
  }
}

// Helper functions

const parseDates = (data: SherlockDruidDataAttributes[]) => {
  if (!Array.isArray(data)) {
    return [];
  }

  return data.map((item: any) => {
    return {
      ...item,
      x: new Date(item.x),
    };
  });
};

const isValidDataArray = (data: any): data is SherlockDruidDataAttributes[] => {
  return Array.isArray(data) && data.length > 0;
};
